/**
 * Factory for creating Axios instances with a shared interceptor that improves stack traces.
 *
 * By default, Axios errors do not include a stack trace pointing back to the calling code —
 * the trace only shows Axios internals. The interceptors here capture a stack trace at the
 * time of the request and append it to any error that is thrown, making it easier to trace
 * errors back to the originating call site.
 *
 * @see https://github.com/axios/axios/issues/2387#issuecomment-652242713
 */
import axios, { AxiosInstance, CreateAxiosDefaults } from 'axios';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    errorContext?: Error;
  }
}

export const axiosClient = createAxiosClient();

export function createAxiosClient(config?: CreateAxiosDefaults): AxiosInstance {
  const instance = axios.create(config);

  instance.interceptors.request.use((config) => {
    config.errorContext = new Error('Thrown at:');
    return config;
  });

  instance.interceptors.response.use(undefined, async (error) => {
    const originalStackTrace = error.config?.errorContext?.stack;
    if (originalStackTrace) {
      error.stack = `${error.stack}\n${originalStackTrace}`;
    }

    throw error;
  });

  return instance;
}
